import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { routes } from './app.routes';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAuth0 } from '@auth0/auth0-angular';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { environment } from '../environments';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { HttpLink } from './lib';
import { TraceService, createErrorHandler } from '@sentry/angular';
import { errorInterceptorFn } from 'app/core/interceptors';
import { MessageService } from 'primeng/api';
import { customAuthInterceptor } from './core/interceptors/customAuthInterceptor';
import { provideNgxStripe } from 'ngx-stripe';

const initialSearchParams = new URLSearchParams(window.location.search ?? '');
const initialPromoCode = initialSearchParams.get('promoCode');
if (initialPromoCode) {
  sessionStorage.setItem('promoCode', initialPromoCode);
}

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{sky.50}',
      100: '{sky.100}',
      200: '{sky.200}',
      300: '{sky.300}',
      400: '{sky.400}',
      500: '{sky.500}',
      600: '{sky.600}',
      700: '{sky.700}',
      800: '{sky.800}',
      900: '{sky.900}',
      950: '{sky.950}',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useValue: createErrorHandler() },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [TraceService],
      multi: true,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(routes),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: 'wl-dark',
        },
      },
    }),
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientID,
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
      useRefreshTokensFallback: true,
      authorizationParams: {
        audience: environment.auth0.audience,
        scope: environment.auth0.scope,
        redirect_uri: window.location.origin,
        'ext-promoCode': sessionStorage.getItem('promoCode') ?? undefined,
        connection:
          // @ts-ignore
          environment.connections?.[new URLSearchParams(window.location.search ?? '').get('connection') ?? '']
            ?.auth0Connection,
      },
      authorizeTimeoutInSeconds: 120,
      httpTimeoutInSeconds: 120,
      legacySameSiteCookie: true,
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (http: HttpClient) => {
            return new TranslateHttpLoader(http, '/assets/i18n/', `.json?v=${APP_VERSION}`);
          },
          deps: [HttpClient],
        },
        defaultLanguage: 'en',
      })
    ),
    {
      provide: HttpLink,
    },
    MessageService,
    provideHttpClient(withInterceptors([customAuthInterceptor, errorInterceptorFn])),
    provideNgxStripe(),
  ],
};
